// --- Marian ONNX + Transformers.js Offline Translation Service ---
// Handles model downloading, CacheStorage persistence (marian-onnx-models-v1),
// and 100% offline neural translation using ONNX Runtime Web via Transformers.js

import { pipeline, env } from '@xenova/transformers';

const R2_MARIAN_ROOT = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-translator-models/marian-offline/';
const R2_MARIAN_BASE = `${R2_MARIAN_ROOT}Xenova/`;
const CACHE_NAME = 'marian-onnx-models-v1';



// Configure Transformers.js for 100% local/offline execution
env.allowRemoteModels = false;
env.allowLocalModels = true;
env.localURL = R2_MARIAN_BASE;
env.useBrowserCache = true;

// Disable multi-threading WASM web worker warnings if not supported
if (env.backends?.onnx?.wasm) {
  env.backends.onnx.wasm.numThreads = 1;
}

export interface MarianModelMetadata {
  modelName: string;
  sourceLang: string;
  targetLang: string;
  byteSize?: number;
}

const MODEL_MAP: Record<string, string> = {
  'en-hu': 'opus-mt-en-hu',
  'hu-en': 'opus-mt-hu-en',
  'en-ru': 'opus-mt-en-ru',
  'ru-en': 'opus-mt-ru-en',
  'en-it': 'opus-mt-en-it',
  'it-en': 'opus-mt-it-en',
  'en-es': 'opus-mt-en-es',
  'es-en': 'opus-mt-es-en',
  'en-fr': 'opus-mt-en-fr',
  'fr-en': 'opus-mt-fr-en',
};

const REQUIRED_FILES = [
  'config.json',
  'generation_config.json',
  'tokenizer.json',
  'tokenizer_config.json',
  'onnx/encoder_model_quantized.onnx',
  'onnx/decoder_model_merged_quantized.onnx'
];

// In-memory cache of loaded Translation Pipelines
const activePipelines = new Map<string, any>();

export const getModelFolderForPair = (sourceLang: string, targetLang: string): string | null => {
  const src2 = sourceLang.toLowerCase().split('-')[0];
  const tgt2 = targetLang.toLowerCase().split('-')[0];
  const key = `${src2}-${tgt2}`;
  return MODEL_MAP[key] || null;
};

const fetchAndCacheFile = async (url: string, cache: Cache): Promise<void> => {
  console.log(`[MarianModelService Diag] Initiating fetch for URL: "${url}"`);
  const existing = await cache.match(url);
  if (existing) {
    console.log(`[MarianModelService Diag] Found cached response for URL: "${url}"`);
    return;
  }

  let retries = 3;
  while (retries > 0) {
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit'
      });
      
      const contentType = response.headers.get('content-type') || 'unknown';
      console.log(`[MarianModelService Diag] HTTP Response for "${url}": status=${response.status} (${response.statusText}), contentType="${contentType}"`);

      if (response.status === 404 || contentType.includes('text/html')) {
        const errorText = await response.text();
        console.error(`[MarianModelService Diag ERROR] 404/HTML error for URL: "${url}" | Preview:`, errorText.slice(0, 200));
        throw new Error(`HTTP ${response.status} (${contentType}) ao carregar ${url}`);
      }

      if (response.ok) {
        if (response.type === 'opaque') {
          throw new Error(`Resposta opaca (CORS) recebida para ${url}`);
        }
        await cache.put(url, response.clone());
        // Also mirror into transformers-cache so Transformers.js internal cache finds it
        try {
          const tfCache = await caches.open('transformers-cache');
          await tfCache.put(url, response.clone());
        } catch (_) {}
        return;
      }
    } catch (e) {
      console.warn(`[MarianModelService Diag] Fetch retry (${retries - 1} left) for ${url}:`, e);
    }
    retries--;
    if (retries > 0) {
      await new Promise(r => setTimeout(r, 1000));
    }
  }
  throw new Error(`Falha ao baixar o arquivo do modelo: ${url}`);
};

export interface MarianLanguagePair {
  id: string;
  name: string;
  flag: string;
  folders: string[];
  sizeMB: number;
}

export const MARIAN_LANGUAGE_PAIRS: MarianLanguagePair[] = [
  { id: 'en', name: 'Inglês (en_US - Idioma Pivô)', flag: '🇺🇸', folders: ['opus-mt-fr-en', 'opus-mt-es-en', 'opus-mt-it-en', 'opus-mt-ru-en', 'opus-mt-hu-en'], sizeMB: 390 },
  { id: 'fr', name: 'Francês (fr_FR)', flag: '🇫🇷', folders: ['opus-mt-en-fr', 'opus-mt-fr-en'], sizeMB: 156 },
  { id: 'es', name: 'Espanhol (es_ES)', flag: '🇪🇸', folders: ['opus-mt-en-es', 'opus-mt-es-en'], sizeMB: 156 },
  { id: 'it', name: 'Italiano (it_IT)', flag: '🇮🇹', folders: ['opus-mt-en-it', 'opus-mt-it-en'], sizeMB: 156 },
  { id: 'ru', name: 'Russo (ru_RU)', flag: '🇷🇺', folders: ['opus-mt-en-ru', 'opus-mt-ru-en'], sizeMB: 156 },
  { id: 'hu', name: 'Húngaro (hu_HU)', flag: '🇭🇺', folders: ['opus-mt-en-hu', 'opus-mt-hu-en'], sizeMB: 156 },
];

export const checkPairDownloaded = async (folders: string[]): Promise<boolean> => {
  if (typeof caches === 'undefined') return false;
  try {
    const cache = await caches.open(CACHE_NAME);
    for (const folder of folders) {
      for (const file of REQUIRED_FILES) {
        const fileUrl = `${R2_MARIAN_BASE}${folder}/${file}`;
        const match = await cache.match(fileUrl);
        if (!match) return false;
      }
    }
    return true;
  } catch (e) {
    return false;
  }
};

export const downloadPair = async (
  folders: string[],
  onProgress: (percent: number) => void
): Promise<void> => {
  if (typeof caches === 'undefined') {
    throw new Error('Cache Storage não suportado neste navegador.');
  }

  const cache = await caches.open(CACHE_NAME);
  const urls: string[] = [];
  for (const folder of folders) {
    for (const file of REQUIRED_FILES) {
      urls.push(`${R2_MARIAN_BASE}${folder}/${file}`);
    }
  }

  const checkedStatus = await Promise.all(urls.map(async (url) => {
    const match = await cache.match(url);
    return { url, cached: !!match };
  }));

  let completed = checkedStatus.filter(x => x.cached).length;
  onProgress(Math.round((completed / urls.length) * 100));

  const toDownload = checkedStatus.filter(x => !x.cached).map(x => x.url);
  if (toDownload.length === 0) {
    onProgress(100);
    return;
  }

  const CONCURRENCY = 2;
  const downloadWorker = async () => {
    while (toDownload.length > 0) {
      const url = toDownload.shift();
      if (url) {
        await fetchAndCacheFile(url, cache);
        completed++;
        onProgress(Math.round((completed / urls.length) * 100));
      }
    }
  };

  const workers = Array(Math.min(CONCURRENCY, toDownload.length)).fill(null).map(downloadWorker);
  await Promise.all(workers);
};

export const deletePair = async (folders: string[]): Promise<void> => {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open(CACHE_NAME);
    const tfCache = await caches.open('transformers-cache');
    for (const folder of folders) {
      activePipelines.delete(folder);
      for (const file of REQUIRED_FILES) {
        const fileUrl = `${R2_MARIAN_BASE}${folder}/${file}`;
        await cache.delete(fileUrl);
        await tfCache.delete(fileUrl);
      }
    }
  } catch (e) {
    console.warn('[MarianModelService] deletePair error:', e);
  }
};

export const checkAllModelsDownloaded = async (): Promise<boolean> => {
  if (typeof caches === 'undefined') return false;
  try {
    const cache = await caches.open(CACHE_NAME);
    for (const modelFolder of Object.values(MODEL_MAP)) {
      for (const file of REQUIRED_FILES) {
        const fileUrl = `${R2_MARIAN_BASE}${modelFolder}/${file}`;
        const match = await cache.match(fileUrl);
        if (!match) {
          return false;
        }
      }
    }
    return true;
  } catch (e) {
    console.warn('[MarianModelService] checkAllModelsDownloaded error:', e);
    return false;
  }
};

export const downloadAllBergamotModels = async (
  onProgress: (percent: number) => void
): Promise<void> => {
  if (typeof caches === 'undefined') {
    throw new Error('Cache Storage não suportado neste navegador.');
  }

  const cache = await caches.open(CACHE_NAME);
  
  const allUrls: string[] = [];
  for (const modelFolder of Object.values(MODEL_MAP)) {
    for (const file of REQUIRED_FILES) {
      allUrls.push(`${R2_MARIAN_BASE}${modelFolder}/${file}`);
    }
  }

  const checkedStatus = await Promise.all(allUrls.map(async (url) => {
    const match = await cache.match(url);
    return { url, cached: !!match };
  }));

  let completed = checkedStatus.filter(x => x.cached).length;
  onProgress(Math.round((completed / allUrls.length) * 100));

  const toDownload = checkedStatus.filter(x => !x.cached).map(x => x.url);

  if (toDownload.length === 0) {
    onProgress(100);
    return;
  }

  const CONCURRENCY = 2;
  const downloadWorker = async () => {
    while (toDownload.length > 0) {
      const url = toDownload.shift();
      if (url) {
        await fetchAndCacheFile(url, cache);
        completed++;
        onProgress(Math.round((completed / allUrls.length) * 100));
      }
    }
  };

  const workers = Array(Math.min(CONCURRENCY, toDownload.length)).fill(null).map(downloadWorker);
  await Promise.all(workers);
};

export const loadPipelineForModel = async (modelFolder: string): Promise<any> => {
  if (activePipelines.has(modelFolder)) {
    return activePipelines.get(modelFolder);
  }

  console.log(`[MarianModelService Diag] Loading Transformers.js pipeline for ${modelFolder} | env.localURL="${env.localURL}" | allowRemote=${env.allowRemoteModels} | allowLocal=${env.allowLocalModels}`);

  try {
    // Force offline fetch from CacheStorage for local models
    const translator = await pipeline('translation', modelFolder, {
      quantized: true,
      local_files_only: true,
    });

    activePipelines.set(modelFolder, translator);
    return translator;
  } catch (err: any) {
    console.error(`[MarianModelService Diag ERROR] Pipeline initialization failed for ${modelFolder}:`, err);
    throw err;
  }
};

const translateSinglePair = async (
  text: string,
  sourceLang: string,
  targetLang: string
): Promise<string | null> => {
  const src2 = sourceLang.toLowerCase().split('-')[0];
  const tgt2 = targetLang.toLowerCase().split('-')[0];
  const modelFolder = getModelFolderForPair(src2, tgt2);

  if (!modelFolder) {
    console.warn(`[MarianModelService] No direct Marian ONNX model found for ${src2}->${tgt2}`);
    return null;
  }

  try {
    const translator = await loadPipelineForModel(modelFolder);
    const output = await translator(text, {
      src_lang: src2,
      tgt_lang: tgt2,
    });

    if (output && Array.isArray(output) && output[0]?.translation_text) {
      return output[0].translation_text.trim();
    } else if (typeof output === 'string') {
      return output.trim();
    }
  } catch (err) {
    console.error(`[MarianModelService] Error translating with ${modelFolder}:`, err);
    throw err;
  }

  return null;
};

import { nllbModelService, checkNllbModelDownloaded, downloadNllbModel, deleteNllbModel } from './nllbModelService';

export const marianModelService = {
  checkAllModelsDownloaded: checkNllbModelDownloaded,
  downloadAllBergamotModels: downloadNllbModel,
  deleteModel: deleteNllbModel,

  async translateOffline(
    text: string,
    targetLanguage: string,
    sourceLanguage: string = 'en'
  ): Promise<{ translation: string; source: string } | null> {
    return nllbModelService.translateOffline(text, targetLanguage, sourceLanguage);
  }
};
